<script>
// Sidebar do contexto CLIENTE (operacional). Troca por inteiro ao entrar num
// cliente: faixa de accent, botão "Voltar para gestão", o ClientSwitcher no topo
// e os módulos restritos ao cliente selecionado.
import { CLIENTE_GROUPS } from '@modules/navegacao-v2/constants/navigation-modules'
import { visibleGroups, isActiveRoute } from '@modules/navegacao-v2/utils/visible-modules'
import { withClienteSubitems } from '@modules/navegacao-v2/utils/cliente-subitems'
import { clienteAccentColor } from '@modules/navegacao-v2/utils/cliente-accent'
import flyoutHover from '@modules/navegacao-v2/mixins/flyout-hover'
import apiListaClientes from '@/src/api/listaClientes'
import JourneySidebarNext from '@modules/client-journey/components/journey-sidebar-next/journey-sidebar-next.vue'
import SidebarShell from './sidebar-shell.vue'
import ClientSwitcher from './client-switcher.vue'
import NavGroup from './nav-group.vue'
import NavItem from './nav-item.vue'
import NavFlyout from './nav-flyout.vue'

const BACK_ICON_SIZE = 16

export default {
  name: 'ClienteSidebar',
  components: {
    SidebarShell,
    ClientSwitcher,
    JourneySidebarNext,
    NavGroup,
    NavItem,
    NavFlyout,
  },
  mixins: [flyoutHover],
  props: {
    collapsed: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
  },
  data() {
    return { BACK_ICON_SIZE }
  },
  computed: {
    access() {
      return this.$store.getters['parameters/show'] || {}
    },
    isInternal() {
      const user = this.$currentUser()
      return !!(user && user.IS_INTERNAL)
    },
    activeRouteName() {
      return this.$route ? this.$route.name : ''
    },
    groups() {
      const visible = visibleGroups(CLIENTE_GROUPS, this.access, this.isInternal, {
        currentUser: this.$currentUser(),
      })
      return withClienteSubitems(visible, this.access, this.$currentUser())
    },
    // Faixa do topo: primária para cliente, cinza para lead.
    accentColor() {
      return clienteAccentColor(this.$currentClient())
    },
  },
  mounted() {
    // O ClientSwitcher busca clientes no getter `user/allClients`, que só é
    // populado por apiListaClientes.carregar() (dispatch user/setClients).
    // A página Clientes usa API própria, então o switcher fica vazio se o load
    // não for disparado aqui. carregar() é idempotente (guarda this.loading).
    const clients = this.$store.getters['user/allClients'] || []
    if (!clients.length) apiListaClientes.carregar()
  },
  methods: {
    isActive(routeName) {
      return isActiveRoute(routeName, this.activeRouteName)
    },
    // Clique num subitem do flyout: fecha o painel e delega a navegação ao pai.
    onSelectSubitem(subitem) {
      this.closeFlyoutNow()
      this.$emit('select', subitem)
    },
    back() {
      this.$store.dispatch('navigation/backToGestao')
      this.$emit('back')
    },
  },
}
</script>

<template>
  <SidebarShell
    :collapsed="collapsed"
    :mobile="mobile"
    :open="open"
    accent-top
    :accent-color="accentColor"
    @toggle="$emit('toggle')"
  >
    <template v-slot:brand>
      <button
        class="cliente-sidebar__back"
        :class="{ 'cliente-sidebar__back--collapsed': collapsed }"
        type="button"
        title="Voltar para gestão"
        @click="back"
      >
        <icon-svg
          class="cliente-sidebar__back-arrow"
          name="arrow-left"
          :size="BACK_ICON_SIZE"
          color="primary"
        />
        <span v-if="!collapsed">Voltar para gestão</span>
      </button>

      <ClientSwitcher
        :collapsed="collapsed"
        @picked="$emit('picked', $event)"
        @open-all="$emit('open-all')"
      />

      <JourneySidebarNext :collapsed="collapsed" />
    </template>

    <NavGroup
      v-for="group in groups"
      :key="group.title"
      :title="group.title"
      :collapsed="collapsed"
    >
      <NavItem
        v-for="item in group.items"
        :key="item.key"
        :item="item"
        :subitems="item.subitems"
        :collapsed="collapsed"
        :mobile="mobile"
        :active="isActive(item.routeName)"
        @click="$emit('select', item)"
        @hover-subitems="openFlyout"
        @leave-subitems="scheduleClose"
      />
    </NavGroup>

    <transition name="flyout">
      <NavFlyout
        v-if="flyout"
        :title="flyout.item.label"
        :items="flyout.item.subitems"
        :anchor-top="flyout.top"
        :anchor-left="flyout.left"
        :active-route-name="activeRouteName"
        @select="onSelectSubitem"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      />
    </transition>
  </SidebarShell>
</template>

<style lang="sass" scoped>
// O SVG `arrow-left` na verdade aponta para a direita (mal nomeado na lib de
// ícones). Espelhamos para virar uma flecha de "voltar" apontando à esquerda.
.cliente-sidebar__back-arrow
  transform: scaleX(-1)

.cliente-sidebar__back
  display: flex
  align-items: center
  gap: 10px
  margin: 12px 12px 0
  padding: 9px 12px
  border-radius: 10px
  border: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.12)
  background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.03)
  color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.8)
  font-size: 12.5px
  font-weight: 600
  white-space: nowrap
  cursor: pointer

  &:hover
    background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.07)

  &--collapsed
    width: 40px
    justify-content: center
    margin: 12px auto 0
    padding: 9px
</style>
